// src/pages/ElectivosAdmin.js
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Papa from 'papaparse';
import './ElectivosAdmin.css';

export default function ElectivosAdmin() {
  const [users, setUsers]             = useState([]);
  const [loading, setLoading]         = useState(true);
  const [form, setForm]               = useState({
    rut: '', nombre: '', email: '', password: '', role: 'estudiante', curso: 'IIIA'
  });
  const [message, setMessage]         = useState('');
  const [resetModal, setResetModal]   = useState({ open: false, rut: '', pwd: '' });
  const [editModal, setEditModal]     = useState({ open: false, rut: '', nombre: '', curso: '' });

  // — Usuarios bulk upload
  const [bulkFile, setBulkFile]       = useState(null);
  const [bulkMsg, setBulkMsg]         = useState('');

  // ** Notas final bulk upload **
  const [gradesFile, setGradesFile]   = useState(null);
  const [gradesMsg, setGradesMsg]     = useState('');

  // ** filtros para la lista **
  const [filterRole, setFilterRole]   = useState('all');
  const [filterCurso, setFilterCurso] = useState('all');

  const [sortKey, setSortKey]         = useState(null);
  const [sortAsc, setSortAsc]         = useState(true);

  const API = process.env.REACT_APP_API_URL || '';

  // 1) Carga inicial de usuarios
  useEffect(() => { fetchUsers(); }, []);
  async function fetchUsers() {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/student/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(data);
    } catch (err) {
      console.error(err);
      setMessage('Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  }

  // 2) Crear usuario
  async function handleAddUser(e) {
    e.preventDefault();
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/student/admin/users`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Usuario creado ✔️');
      setForm({ rut: '', nombre: '', email: '', password: '', role: 'estudiante', curso: 'IIIA' });
      fetchUsers();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Error creando usuario');
    }
  }

  // 3) Eliminar usuario
  async function handleDelete(rut) {
    if (!window.confirm(`¿Eliminar usuario ${rut}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/student/admin/users/${encodeURIComponent(rut)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Usuario eliminado ✔️');
      fetchUsers();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Error eliminando usuario');
    }
  }

  // 4) Eliminar todos los Estudiantes
  async function handleDeleteAllStudents() {
    if (!window.confirm('¿Eliminar todos los usuarios con rol Estudiante?')) return;
    try {
      const token = localStorage.getItem('token');
      const estudiantes = users.filter(u => u.role === 'estudiante');
      await Promise.all(estudiantes.map(u =>
        axios.delete(
          `${API}/student/admin/users/${encodeURIComponent(u.rut)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        )
      ));
      setMessage('Todos los estudiantes han sido eliminados ✔️');
      fetchUsers();
    } catch (err) {
      console.error(err);
      setMessage('Error borrando estudiantes');
    }
  }

  // 5) Reset contraseña
  function openReset(rut) {
    setMessage('');
    setResetModal({ open: true, rut, pwd: '' });
  }
  function closeReset() {
    setResetModal({ open: false, rut: '', pwd: '' });
  }
  async function handleResetSubmit(e) {
    e.preventDefault();
    if (!resetModal.pwd) {
      setMessage('Ingresa nueva contraseña');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/student/admin/users/${encodeURIComponent(resetModal.rut)}/password`,
        { password: resetModal.pwd },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Contraseña restablecida ✔️');
      closeReset();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Error restableciendo contraseña');
    }
  }

  // 6) Editar nombre, curso
  function openEdit(u) {
    setMessage('');
    setEditModal({ open: true, rut: u.rut, nombre: u.nombre, curso: u.curso || 'IIIA' });
  }
  function closeEdit() {
    setEditModal({ open: false, rut: '', nombre: '', curso: '' });
  }
  async function handleEditSubmit(e) {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/student/admin/users/${encodeURIComponent(editModal.rut)}`,
        { nombre: editModal.nombre, curso: editModal.curso },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage('Usuario actualizado ✔️');
      closeEdit();
      fetchUsers();
    } catch (err) {
      console.error(err);
      setMessage(err.response?.data?.message || 'Error actualizando usuario');
    }
  }

  // 7) Ordenamiento de columnas
  const sortedUsers = useMemo(() => {
    if (!sortKey) return users;
    return [...users].sort((a, b) => {
      const A = (a[sortKey] || '').toString().toLowerCase();
      const B = (b[sortKey] || '').toString().toLowerCase();
      if (A < B) return sortAsc ? -1 : 1;
      if (A > B) return sortAsc ? 1 : -1;
      return 0;
    });
  }, [users, sortKey, sortAsc]);
  const onHeaderClick = key => {
    if (sortKey === key) setSortAsc(!sortAsc);
    else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  // 8) Bulk CSV upload usuarios (con email)
  function handleFileChange(e) {
    setBulkFile(e.target.files[0]);
    setBulkMsg('');
  }
  function handleBulkUpload() {
    if (!bulkFile) {
      setBulkMsg('Selecciona un archivo CSV primero');
      return;
    }
    Papa.parse(bulkFile, {
      header: true,
      skipEmptyLines: true,
      encoding: "utf-8",
      complete: async results => {
        const rows = results.data.map(r => ({
          rut:       r.Rut?.trim(),
          nombre:    r.Nombre?.trim(),
          email:     r.Email?.trim() || null,
          role:      r.Rol?.trim() || 'estudiante',
          curso:     r.Curso?.trim() || null,
          password:  r.Contraseña?.trim() || r.Rut?.trim(),
          prev1:     r.Electivo1?.trim() || null,
          prev2:     r.Electivo2?.trim() || null,
          prev3:     r.Electivo3?.trim() || null
        }));
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API}/student/admin/users/bulk`,
            { users: rows },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setBulkMsg('CSV importado con éxito ✔️');
          fetchUsers();
        } catch (err) {
          console.error(err);
          setBulkMsg(err.response?.data?.message || 'Error importando CSV');
        }
      },
      error: err => {
        console.error(err);
        setBulkMsg('Error leyendo CSV');
      }
    });
  }

  // 9) Descargar plantilla CSV usuarios (con email)
  function handleTemplateDownload() {
    const header = [
      'Rut', 'Nombre', 'Email', 'Rol', 'Curso',
      'Contraseña', 'Electivo1', 'Electivo2', 'Electivo3'
    ];
    const csvContent = Papa.unparse({ fields: header, data: [] }, { delimiter: ';' });
    const csvWithBom  = '\uFEFF' + csvContent;
    const blob = new Blob([csvWithBom], { type: 'text/csv;charset=utf-8;' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = 'plantilla_usuarios.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // —— Bulk CSV upload notas finales ——
  function handleGradesFileChange(e) {
    setGradesFile(e.target.files[0]);
    setGradesMsg('');
  }
  function handleGradesUpload() {
    if (!gradesFile) {
      setGradesMsg('Selecciona un archivo CSV primero');
      return;
    }
    Papa.parse(gradesFile, {
      header: true,
      skipEmptyLines: true,
      complete: async results => {
        const rows = results.data.map(r => ({
          rut:        r.Rut?.trim(),
          nota_final: parseFloat(r.NotaFinal) || null
        }));
        try {
          const token = localStorage.getItem('token');
          await axios.post(
            `${API}/student/admin/users/grades/bulk`,
            { grades: rows },
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setGradesMsg('Notas importadas con éxito ✔️');
          fetchUsers();
        } catch (err) {
          console.error(err);
          setGradesMsg(err.response?.data?.message || 'Error importando notas');
        }
      },
      error: err => {
        console.error(err);
        setGradesMsg('Error leyendo CSV');
      }
    });
  }

  // 10) Descargar plantilla CSV notas finales
  function handleGradesTemplateDownload() {
    const header = ['Rut','NotaFinal'];
    const csv    = Papa.unparse({ fields: header, data: [] }, { delimiter: ';' });
    const blob   = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url    = URL.createObjectURL(blob);
    const a      = document.createElement('a');
    a.href       = url;
    a.download   = 'plantilla_notas_finales.csv';
    a.click();
    URL.revokeObjectURL(url);
  }

  // 11) Filtrar la lista
  const filteredUsers = useMemo(() => {
    return sortedUsers.filter(u => {
      if (filterRole !== 'all'  && u.role !== filterRole) return false;
      if (filterCurso !== 'all' && u.curso !== filterCurso) return false;
      return true;
    });
  }, [sortedUsers, filterRole, filterCurso]);

  if (loading) return <p className="admin-loading">Cargando usuarios…</p>;

  return (
    <div className="admin-container">
      <h1>Panel Electivos Admin – Gestión de Usuarios</h1>
      {message && <div className="admin-message">{message}</div>}

      {/* 1) Crear Usuario */}
      <section className="admin-section">
        <h2>1) Crear Usuario</h2>
        <div className="card">
          <div className="card-header">Nuevo Usuario</div>
          <div className="card-body">
            <form onSubmit={handleAddUser} className="admin-form">
              <label>
                Nombre:
                <input
                  type="text"
                  value={form.nombre}
                  onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                  required
                />
              </label>
              <label>
                RUT:
                <input
                  type="text"
                  value={form.rut}
                  onChange={e => setForm(f => ({ ...f, rut: e.target.value }))}
                  required
                />
              </label>
              <label>
                Email:
                <input
                  type="email"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  required
                />
              </label>
              <label>
                Contraseña:
                <input
                  type="password"
                  value={form.password}
                  onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                  required
                />
              </label>
              <label>
                Rol:
                <select
                  value={form.role}
                  onChange={e => setForm(f => ({ ...f, role: e.target.value }))}
                >
                  <option value="estudiante">Estudiante</option>
                  <option value="administrador_student_module">Electivos Admin</option>
                </select>
              </label>
              {form.role === 'estudiante' && (
                <label>
                  Curso:
                  <select
                    value={form.curso}
                    onChange={e => setForm(f => ({ ...f, curso: e.target.value }))}
                  >
                    {['IIA','IIB','IIIA','IIIB'].map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </label>
              )}
              <button type="submit" className="btn-primary">Crear Usuario</button>
            </form>
          </div>
        </div>
      </section>

      {/* 2) Importar Usuarios desde CSV */}
      <section className="admin-section">
        <h2>2) Importar Usuarios desde CSV</h2>
        <div className="card">
          <div className="card-header">Bulk Upload CSV</div>
          <div className="card-body">
            <button
              className="btn-secondary"
              onClick={handleTemplateDownload}
              style={{ marginBottom: '0.5rem' }}
            >
              Descargar plantilla CSV
            </button>
            <input
              type="file"
              accept=".csv"
              onChange={handleFileChange}
              style={{ marginTop: '0.5rem' }}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '0.5rem' }}
              onClick={handleBulkUpload}
            >
              Importar CSV
            </button>
            {bulkMsg && <p className="message">{bulkMsg}</p>}
            <small>
              Columnas esperadas:<br/>
              <strong>
                Rut;Nombre;Email;Rol;Curso;Contraseña;Electivo1;Electivo2;Electivo3
              </strong>
            </small>
          </div>
        </div>
      </section>

      {/* 3) Importar Notas Finales desde CSV */}
      <section className="admin-section">
        <h2>3) Importar Notas Finales desde CSV</h2>
        <div className="card">
          <div className="card-header">Bulk Upload Notas Finales</div>
          <div className="card-body">
            <button
              className="btn-secondary"
              onClick={handleGradesTemplateDownload}
              style={{ marginBottom: '0.5rem' }}
            >
              Descargar plantilla CSV
            </button>
            <input
              type="file"
              accept=".csv"
              onChange={handleGradesFileChange}
              style={{ marginTop: '0.5rem' }}
            />
            <button
              className="btn-primary"
              style={{ marginTop: '0.5rem' }}
              onClick={handleGradesUpload}
            >
              Importar Notas
            </button>
            {gradesMsg && <p className="message">{gradesMsg}</p>}
            <small>
              Columnas esperadas:<br/>
              <strong>Rut;NotaFinal</strong>
            </small>
          </div>
        </div>
      </section>

      {/* 4) Lista de Usuarios con filtros */}
      <section className="admin-section">
        <h2>4) Lista de Usuarios</h2>
        <div className="filter-controls" style={{ marginBottom: '1rem' }}>
          <label style={{ marginRight: '1rem' }}>
            Filtrar por rol:&nbsp;
            <select
              value={filterRole}
              onChange={e => setFilterRole(e.target.value)}
            >
              <option value="all">Todos</option>
              <option value="estudiante">Estudiante</option>
              <option value="administrador_student_module">Electivos Admin</option>
            </select>
          </label>
          <label>
            Filtrar por curso:&nbsp;
            <select
              value={filterCurso}
              onChange={e => setFilterCurso(e.target.value)}
            >
              <option value="all">Todos</option>
              {['IIA','IIB','IIIA','IIIB'].map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </label>
        </div>

        <table className="admin-table">
          <thead>
            <tr>
              <th onClick={() => onHeaderClick('rut')}>RUT</th>
              <th onClick={() => onHeaderClick('nombre')}>Nombre</th>
              <th onClick={() => onHeaderClick('email')}>Email</th>
              <th onClick={() => onHeaderClick('role')}>Rol</th>
              <th onClick={() => onHeaderClick('curso')}>Curso</th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(u => (
              <tr key={u.rut}>
                <td>{u.rut}</td>
                <td>{u.nombre}</td>
                <td>{u.email || '-'}</td>
                <td>
                  {u.role === 'administrador_student_module'
                    ? 'Electivos Admin'
                    : 'Estudiante'}
                </td>
                <td>{u.role === 'estudiante' ? (u.curso || '-') : '-'}</td>
                <td className="actions-cell">
                  <button
                    className="btn-icon"
                    title="Editar"
                    onClick={() => openEdit(u)}
                  >✏️</button>
                  <button
                    className="btn-icon"
                    title="Reset contraseña"
                    onClick={() => openReset(u.rut)}
                  >🔒</button>
                  <button
                    className="btn-icon delete"
                    title="Eliminar usuario"
                    onClick={() => handleDelete(u.rut)}
                  >🗑️</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Botón para borrar todos los Estudiantes */}
        <div style={{ marginTop: '1rem' }}>
          <button className="btn-danger" onClick={handleDeleteAllStudents}>
            Borrar todos los Estudiantes
          </button>
        </div>
      </section>

      {/* Modales (reset / edit) */}
      {resetModal.open && (
        <div className="modal-overlay" onClick={closeReset}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Reset contraseña: {resetModal.rut}</h3>
            <form onSubmit={handleResetSubmit}>
              <input
                type="password"
                placeholder="Nueva contraseña"
                value={resetModal.pwd}
                onChange={e => setResetModal(m => ({ ...m, pwd: e.target.value }))}
                autoFocus
              />
              <div className="modal-buttons">
                <button type="submit">Guardar</button>
                <button type="button" onClick={closeReset}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {editModal.open && (
        <div className="modal-overlay" onClick={closeEdit}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h3>Editar usuario: {editModal.rut}</h3>
            <form onSubmit={handleEditSubmit}>
              <label>
                Nombre
                <input
                  type="text"
                  value={editModal.nombre}
                  onChange={e => setEditModal(m => ({ ...m, nombre: e.target.value }))}
                  required
                />
              </label>
              <label>
                Curso
                <select
                  value={editModal.curso}
                  onChange={e => setEditModal(m => ({ ...m, curso: e.target.value }))}
                >
                  {['IIA','IIB','IIIA','IIIB'].map(c => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
              <div className="modal-buttons">
                <button type="submit">Guardar</button>
                <button type="button" onClick={closeEdit}>Cancelar</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
